/**
 * useInstructorTier — Feb 6, 2026.
 *
 * Resolves the authenticated instructor's tier to one of:
 *   "basic"   — Basic plan (£19) or no instructor subscription
 *   "premium" — Premium plan (£39)
 *   "growth"  — Growth plan (£59)
 *
 * The backend uses legacy plan_ids for billing continuity:
 *   starter → basic   (kept for active subscribers)
 *   pro     → premium
 *   elite   → growth
 *
 * Anything else (free, legacy learner plans, missing) is treated as
 * "basic" so the user always sees a working app even without a paid
 * tier — locked tiles guide them to upgrade.
 *
 * Dev-only override (Feb 6, 2026): if `localStorage.dev_tier_override`
 * is set to "basic" / "premium" / "growth" AND the current user is
 * admin, the override wins. Used by the dev TierSwitcher to preview
 * each tier without changing real billing. Non-admin users CANNOT
 * trigger this override even if they manually set the localStorage
 * key — every read re-checks the auth role.
 *
 * Usage:
 *   const { tier, has, planLabel, devOverride } = useInstructorTier();
 *   if (!has("growth")) { ... }
 */
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

const TIER_RANK = { basic: 0, premium: 1, growth: 2 };

const PLAN_TO_TIER = {
  starter: "basic",
  basic:   "basic",
  pro:     "premium",
  premium: "premium",
  elite:   "growth",
  growth:  "growth",
};

const TIER_LABEL = {
  basic:   "Basic",
  premium: "Premium",
  growth:  "Growth",
};

export const DEV_TIER_OVERRIDE_KEY = "dev_tier_override";
export const DEV_TIER_EVENT = "dev_tier_override_changed";

function readDevOverride() {
  try {
    const raw = localStorage.getItem(DEV_TIER_OVERRIDE_KEY);
    if (raw && Object.prototype.hasOwnProperty.call(TIER_RANK, raw)) return raw;
  } catch {}
  return null;
}

export function useInstructorTier() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  // Subscribe to localStorage changes so a TierSwitcher tap re-renders
  // every gated component immediately, without page reload.
  const [override, setOverride] = useState(() => (isAdmin ? readDevOverride() : null));
  useEffect(() => {
    if (!isAdmin) {
      // Defensive: if the user is no longer admin, kill the override.
      if (override) setOverride(null);
      return;
    }
    const sync = () => setOverride(readDevOverride());
    window.addEventListener("storage", sync);
    window.addEventListener(DEV_TIER_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(DEV_TIER_EVENT, sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const planId = (user?.plan || "").toLowerCase();
  const realTier = PLAN_TO_TIER[planId] || "basic";
  const tier = (isAdmin && override) ? override : realTier;

  const has = (required) => {
    const need = TIER_RANK[required] ?? 0;
    const got = TIER_RANK[tier] ?? 0;
    return got >= need;
  };

  return {
    tier,                                  // "basic" | "premium" | "growth"
    planLabel: TIER_LABEL[tier],           // "Basic" | "Premium" | "Growth"
    realTier,                              // tier from billing, ignoring override
    devOverride: isAdmin ? override : null,
    has,                                   // has("premium") → bool
  };
}
